<template>
  <ResizableColumns identifier="task-edit-page" :first-column-min-size="27"  :first-column-default-size="27">
    <template #first-column>
      <div class="cols">
        <div v-if="canAcceptTask">
          <button class="green-gradient width-100p mb-5" @click="acceptTask">
            {{ $t('task.accept') }}
          </button>
        </div>
        <div v-if="data?.acceptedByDriver" class="mb-5 font-size-15 font-bold text-center">
          {{ $t('task.status.accepted-by-driver') }}
        </div>
        <form class="cols" @submit.prevent="saveTask">
          <div class="mb-10">
            <div class="cols pr-10">
              <label for="name">
                {{ $t('task.name') }}
              </label>
              <input
                  id="name"
                  v-model="name"
                  maxlength="128"
                  :disabled="!canEdit"
                  required
              />
            </div>
          </div>
          <div class="mb-10">
            <div class="cols pr-10">
              <label for="description">
                {{ $t('task.description') }}
              </label>
              <textarea
                  id="description"
                  v-model="description"
                  maxlength="512"
                  :disabled="!canEdit"
                  required />
            </div>
          </div>
          <div class="rows mb-10">
            <div class="half cols pr-10">
              <label for="driver">
                {{ $t('task.driver') }}
              </label>
              <ChooseDriver id="driver"
                            class="drivers"
                            v-model="driver"
                            @update:modelValue="onDriverUpdated"
                            :disabled="!canEdit">
              </ChooseDriver>
            </div>
            <div class="half cols pr-10">
              <label for="vehicle">{{ $t('task.vehicle') }}</label>
              <ChooseVehicles id="vehicle"
                              class="vehicles"
                              :preselectAll="false"
                              :single-select="true"
                              v-model="vehicle"
                              :disabled="!canEditVehicle"
                              @update:modelValue="onVehicleUpdated">
              </ChooseVehicles>
            </div>
          </div>
          <div class="rows mb-10">
            <div class="half cols pr-10">
              <label for="driverPhoneNumber">
                {{ $t('settings.on-board.users.phone-number') }}
              </label>
              <input
                  id="driverPhoneNumber"
                  v-model="driverPhoneNumber"
                  :disabled="!canEditDriverPhoneNumber"
                  v-maska data-maska="+359#########" placeholder="+359888888888"
              />
            </div>
          </div>
          <div class="rows mb-10">
            <div class="half cols pr-10">
              <label for="startDate">{{ $t('task.start-date') }}</label>
              <Datepicker id="startDate"
                          v-model="startDate"
                          :format="$t('settings.generic.date-time-format')"
                          :locale="locale"
                          :min-date="new Date()"
                          :clearable="false"
                          required
                          auto-apply
                          :disabled="!isCreateMode" />
            </div>
            <div class="half cols">
              <label for="scheduleDate">{{ $t('task.schedule-date') }}</label>
              <Datepicker id="scheduleDate"
                          v-model="scheduleDate"
                          :format="$t('settings.generic.date-time-format')"
                          :locale="locale"
                          :min-date="startDate"
                          :clearable="false"
                          required
                          auto-apply
                          :disabled="!isCreateMode" />
            </div>
          </div>
          <div v-if="canEdit || canEditVehicle">
            <button type="button" @click="cancelButtonClick" v-if="isEditMode && isDataEdited">
              {{ $t('generic.cancel') }}
            </button>
            <button type="submit" class="button-save" :disabled="(!isCreateMode && !isEditMode) || !isDataEdited">
              {{ $t('generic.save') }}
            </button>
          </div>
        </form>
        <div class="rows" v-if="taskInProgress || taskFinished">
          <label class="pr-5">{{ $t('task.visited-count') }}: {{ visitedStopsCount }}</label>
          |
          <label class="pl-5">{{ $t('task.non-visited-count') }}: {{ nonVisitedStopsCount }}</label>
        </div>
        <div class="rows">
          <DataGrid
              v-if="canEdit"
              ref="availableGeoFencesList"
              class="ml-0 mr-0 geo-fence-list available-geofences strech fit-content fixed-header"
              :data-model="availableGeoFencesDataGridModel"
              :data="filteredUserGeoFences"
              :selected-id="selectedAvailableGeoFenceId"
              :show-actions-table-header="false"
              @on-select="availableGeoFenceDataGridSelect"
              @on-add-on-list="geoFenceDataAddOnList"
              allow-select
              :allow-add-in-list="!readOnly"
          />
          <DraggableDataGrid
              class="ml-5 mr-0 geo-fence-list selected-geofences strech fit-content fixed-header"
              :data-model="selectedGeoFencesDataGridModel"
              :model-value="selectedGeoFencesFromUser"
              :selected-id="selectedUserGeoFenceId"
              :show-actions-table-header="false"
              @on-select="selectedGeoFenceDataGridSelect"
              @on-remove-from-list="geoFenceDataRemoveFromList"
              @drag-change="onDragChange"
              allow-select
              :allow-drag="canEdit"
              :allow-remove-from-list="canEdit"
          />
        </div>
      </div>
    </template>
    <template #second-column>
      <div class="map-container">
        <Map ref="googleMapRef"
             class="map"
             mapTypeId="terrain"
             :center="center"
             :zoom="15"
             :enable-measure-tool="true"
             scrollwheel
             @on-map-ready="onMapReady">
          <CustomControl v-if="!readOnly" position="TOP_LEFT" :index="1" class="task-map-control">
            <button type="button" @click="addGeoFenceAtMapCenter">
              {{ $t('geo-fences.add') }}
            </button>
          </CustomControl>
          <CustomControl v-if="showTripsSlider" position="TOP_LEFT" :index="2" class="task-slider-control">
            <PositionsSlider
                class="task-positions-slider"
                v-model="selectedPosition"
                :slider-positions="sliderPositions"
                @change="onPositionSliderChange"
            />
          </CustomControl>
          <Marker v-for="(addedGeoFence, index) in addedGeoFenceMarkersOptions" v-bind:key="index"
                  :options="addedGeoFence" @click="openGeoFence(addedGeoFence.geofenceId)">
          </Marker>
          <CircleGeoFence v-for="geoFenceData in userGeoFences" v-bind:key="geoFenceData.id"
                          ref="geoFenceAreasRef"
                          :geoFenceData="geoFenceData"
                          :allow-edit="!readOnly"
                          :hover-enabled="false"
                          @click="geoFenceAreaClick"
                          @saved="onGeoFenceSaved"
          />
          <CircleGeoFence v-if="newGeoFenceData"
                          ref="newGeofenceAreaRef"
                          :geoFenceData="newGeoFenceData"
                          :allow-edit="true"
                          :hover-enabled="false"
                          @click="geoFenceAreaClick"
                          @created="onNewGeoFenceCreated"
                          @cancel_edit_mode="cancelGeoFenceCreate"
          />
          <MapRoute v-for="route in tripsRecordPositionsWithStops"
                    :key="`task-route-${route.index}`"
                    ref="mapRoutes"
                    :positions="route.positions"
                    :stay="route.stay"
                    :stops="route.stops"
                    :trip="route.trip"
                    :index="route.index"
                    @selected="onTripSelected" />
          <VehicleMarker v-if="selectedVehicleCurrentPosition" ref="vehicleMarkerRef"
                         :positionData="selectedVehicleCurrentPosition" :basic-mode="true"
                         :auto-center="false"/>
        </Map>
      </div>
    </template>
  </ResizableColumns>
</template>

<script>

import {mapState} from "vuex";
import {dataAggregator, dataManipulator, entityDataManager, GoogleMapsManager, WorkflowManager} from "@/assets/js/data-manipulation";
import DataGrid from "@/components/pages/partials/DataGrid.vue";
import DraggableDataGrid from "@/components/pages/partials/DraggableDataGrid.vue";
import Map from "@/components/pages/partials/maps/Map.vue"
import CircleGeoFence from "@/components/pages/partials/maps/CircleGeoFence.vue";
import {popups} from "@/assets/js/popups";
import {actions} from "@/assets/js/vue/vuex/actions";
import notification from "@/assets/js/notification";
import ResizableColumns from "@/components/pages/partials/ResizableColumns.vue";
import ChooseVehicles from "@/components/pages/partials/ChooseVehicles.vue";
import {TaskStatus} from "@/assets/js/enums/TaskStatus.ts";
import ChooseDriver from "@/components/pages/partials/ChooseDriver.vue";
import Datepicker from "@vuepic/vue-datepicker";
import locale from "@/assets/js/locale";
import {CustomControl, Marker} from "vue3-google-map";
import {vMaska} from "maska/dist/vue";
import moment from "moment/moment";
import VehicleMarker from "@/components/pages/partials/maps/VehicleMarker.vue";
import MapRoute from "@/components/pages/partials/maps/MapRoute.vue";
import {GeoFenceType} from "@/assets/js/enums/GeoFenceType.ts";
import {GeoFenceColor} from "@/assets/js/enums/GeoFenceColor.ts";
import PositionsSlider from "@/components/pages/partials/PositionsSlider.vue";

export default {
  components: {PositionsSlider, MapRoute, VehicleMarker, Datepicker, ChooseVehicles, ChooseDriver, CircleGeoFence, DataGrid, DraggableDataGrid, Map, ResizableColumns, Marker, CustomControl},
  directives: { maska: vMaska },
  name: 'EditTaskPage',
  props: {
    searchToken: String,
    data: {
      type: [Object, null],
      default: null
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    canAcceptTask: {
      type: Boolean,
      default: false
    },
    secretKey: [String, null]
  },
  emits: ['task-accepted', 'task-saved'],
  data() {
    const copyId = Number.parseInt(this.$route.query['copy-from'], 10);
    return {
      center: GoogleMapsManager.defaultCenter,
      locale: locale.getLocale(),
      copyId: Number.isInteger(copyId) ? copyId : null,

      addedGeoFenceAreas: [],
      newGeoFenceData: null,

      selectedAvailableGeoFenceId: null,
      selectedUserGeoFenceId: null,

      name: null,
      description: null,
      status: TaskStatus.New,
      startDate: moment().startOf('day').add(1, 'day').toDate(),
      scheduleDate: moment().endOf('day').add(1, 'day').toDate(),
      driver: null,
      vehicle: null,
      driverPhoneNumber: null,
      tripsData: null,
      tripsRecordPositions: [],
      selectedTrip: null,
      selectedPosition: null,
    }
  },
  async mounted() {
    if (this.data !== null) {
      this.loadTaskData(this.data);
      await this.loadGeoFences(this.data);
      await this.loadTripsForMapIfAllowed();

      this.onMapReady();
    }
    else if (this.isCopied && this.tasks?.length > 1) {
      await this.copyTask();
    }
  },
  watch: {
    tasks() {
      setTimeout(async () => {
        if (this.isCopied && this.tasks.length > 1) {
          await this.copyTask();
        }
      }, 100);
    },
    userGeoFences() {
      this.syncAddedGeoFenceAreas();
    },
    async data(newData, oldData) {
      if (!dataAggregator.compareObjects(newData, oldData)) {
        this.loadTaskData(this.data);
        await this.loadGeoFences(this.data);
        await this.loadTripsForMapIfAllowed();
      }
    },
  },
  computed: {
    ...mapState(['userGeoFences', 'drivers', 'vehicles', 'positions', 'tasks']),
    isCopied() {
      return this.copyId > 0;
    },
    selectedVehicleCurrentPosition() {
      return this.positions?.filter(position => position.deviceId === this.vehicle?.deviceId)[0] || null;
    },
    tripsRecords() {
      return this.tripsData?.trips ?? [];
    },
    tripsRecordPositionsFlat() {
      return this.tripsRecordPositions.reduce((acc, tripRecordPosition) => [...acc, ...tripRecordPosition], []);
    },
    showTripsSlider() {
      return this.sliderPositions.length > 1;
    },
    sliderPositions() {
      return this.tripsRecordPositionsWithStops.reduce((acc, tripRecordPosition) => {
        const mappedPositions = tripRecordPosition.positions.map(position => {
          const timestamp = dataManipulator.iso8601DateStringToLocalMoment(position.fixTime);

          return {
            value: position.id,
            label: timestamp !== null ? timestamp.format(this.$t('settings.generic.date-time-format-moment')) : '',
            info: {
              tripIndex: tripRecordPosition.index,
            }
          }
        });

        return [...acc, ...mappedPositions];
      }, []);
    },
    tripsRecordPositionsWithStops() {
      const trips = entityDataManager.getTripsRecordPositionsWithStops(
          this.tripsRecords,
          this.tripsRecordPositions,
          this.tripsRecordPositionsFlat,
          this.tripsData?.mapData?.routes,
      );
      const visitedGeofenceIds = new Set(
          this.addedGeoFenceAreas
              .filter(area => area?.visited)
              .map(area => area?.geoFence?.id)
              .filter(id => id != null),
      );

      trips.forEach(trip => {
        trip.stops = trip.stops.filter(stop =>
            !stop?.geofenceIds.some(geofenceId => visitedGeofenceIds.has(geofenceId)),
        );
      });
      return trips;
    },
    addedGeoFenceAreasMappedToDto() {
      return this.addedGeoFenceAreas.map((x, index) => {
        return {
          geofenceId: x.geoFence.id,
          numbering: index + 1
        }
      })
    },
    addedGeoFenceMarkersOptions() {
      const markers = this.addedGeoFenceAreas.map((x, index) => {
        return {
          geoFence: x.geoFence,
          index: index + 1,
          visited: x.visited
        }
      });

      const groupedMarkers = Object.groupBy(markers, x => x.geoFence?.id);

      return Object.keys(groupedMarkers)
          .map(key => {
            const groupedMarker = groupedMarkers[key];
            const geoFence = groupedMarker[0].geoFence;
            const indexes = groupedMarker.map(x => x.index);
            const allVisited = groupedMarker.every(x => x.visited);
            return {
              geofenceId: geoFence?.id,
              position: geoFence?.area?.coordinates,
              icon: {
                path: 'M 10.316406 0.324219 C 9.410156 0.402344 9.128906 0.4375 8.589844 0.550781 C 7.238281 0.835938 6.039062 1.3125 4.929688 2.007812 C 4.273438 2.417969 3.734375 2.84375 3.128906 3.433594 C 2.0625 4.46875 1.234375 5.695312 0.710938 7.007812 C -0.0195312 8.832031 -0.195312 10.992188 0.238281 12.871094 C 0.441406 13.738281 0.867188 14.8125 1.339844 15.636719 C 1.425781 15.78125 1.707031 16.273438 1.972656 16.734375 C 2.71875 18.03125 2.960938 18.410156 4.476562 20.65625 C 6.105469 23.070312 6.738281 24.078125 7.539062 25.523438 C 9.148438 28.425781 10.289062 31.378906 10.945312 34.328125 C 10.984375 34.519531 11.023438 34.664062 11.03125 34.648438 C 11.035156 34.636719 11.097656 34.375 11.160156 34.070312 C 11.957031 30.535156 13.308594 27.367188 15.59375 23.664062 C 16.121094 22.8125 16.578125 22.117188 17.71875 20.425781 C 19.476562 17.816406 19.691406 17.464844 20.601562 15.707031 C 21.324219 14.304688 21.457031 13.984375 21.667969 13.1875 C 21.898438 12.332031 21.980469 11.675781 21.976562 10.71875 C 21.976562 9.804688 21.914062 9.230469 21.730469 8.433594 C 21.171875 5.988281 19.570312 3.703125 17.394531 2.226562 C 15.921875 1.234375 14.097656 0.582031 12.179688 0.375 C 11.824219 0.332031 10.582031 0.300781 10.316406 0.324219 Z M 10.316406 0.324219 ',
                scale: 1,
                fillColor: allVisited ? 'green' : 'red',
                fillOpacity: 0.8,
                strokeWeight: 1,
                anchor: new window.google.maps.Point(12, 24),
                labelOrigin: new window.google.maps.Point(11, 12),
              },
              label: {
                className: 'map-label geofence-label',
                text: `${indexes.join(', ')}`,
              }
            }
          });
    },
    filteredUserGeoFences() {
      return dataAggregator.filterGeoFencesByToken(this.userGeoFences, this.searchToken)
          .sort((a, b) => a.name.localeCompare(b.name));
    },
    availableGeoFencesDataGridModel() {
      return {
        headers: {
          [this.$t('geo-fences.name')]: 'name',
        },
        values: {
          name: item => (item?.name ?? '') + ' - ' + (item?.description ?? ''),
        },
      };
    },
    selectedGeoFencesDataGridModel() {
      const headers = {
        [this.$t('task.number')]: 'numbering',
        [this.$t('geo-fences.name')]: 'name',
      };

      if (this.readOnly || this.taskInProgress || this.taskFinished) {
        headers[this.$t('task.visited')] = 'visited'
      }

      return {
        headers: headers,
        values: {
          numbering: (_, index) => (index + 1),
          name: item => (item.geoFence?.name ?? '') + ' - ' + (item.geoFence?.description ?? ''),
          visited: item => item.visited ? dataManipulator.dateTimeToString(new Date(item.visitTimestamp)) : this.$t('generic.no'),
        },
        class: (item) => {
          if (this.readOnly || this.taskInProgress || this.taskFinished) {
            return [item.visited ? 'visited' : 'not-visited']
          }

          return [];
        }
      };
    },
    selectedGeoFencesFromUser() {
      return this.addedGeoFenceAreas?.map(x => {
        return {
          geoFence: x.geoFence,
          id: x.geoFence?.id,
          visited: x.visited,
          visitTimestamp: x.visitTimestamp
        }
      }) ?? [];
    },
    allStopsCount() {
      return this.data?.taskGeofences?.length ?? 0;
    },
    visitedStopsCount() {
      return this.data?.taskGeofences?.filter(x => x.visited)?.length ?? 0;
    },
    nonVisitedStopsCount() {
      return this.allStopsCount - this.visitedStopsCount;
    },
    taskFinished() {
      return !this.isCreateMode && entityDataManager.isTaskFinished(this.data);
    },
    taskInProgress() {
      return entityDataManager.isTaskInProgress(this.data);
    },
    canEdit() {
      return !this.readOnly && !this.taskFinished && !this.taskInProgress;
    },
    canEditVehicle() {
      return !this.readOnly;
    },
    canEditDriverPhoneNumber() {
      return !this.readOnly;
    },
    isCreateMode() {
      if (this.readOnly) {
        return false;
      }

      return !this.data || this.data?.id === 0;
    },
    isEditMode() {
      if (this.readOnly) {
        return false;
      }

      return this.data?.id > 0;
    },
    isDataEdited() {
      if (this.isCreateMode) {
        return true;
      }

      if (this.data.name !== this.name ||
          this.data.description !== this.description ||
          // should be != due to comparison between null and undefined
          this.data.driverId != this.driver?.id ||
          this.data.vehicleId !== this.vehicle?.id ||
          this.data.driverPhoneNumber !== this.driverPhoneNumber ||
          new Date(this.data.startDate).getTime() !== this.startDate?.getTime() ||
          new Date(this.data.scheduleDate).getTime() !== this.scheduleDate?.getTime() ||
          !entityDataManager.compareTaskGeofences(this.data.taskGeofences, this.addedGeoFenceAreasMappedToDto)) {
        return true;
      }

      return false;
    }
  },
  methods: {
    onMapReady() {
      const geoFencePositions = this.addedGeoFenceAreas?.map(x => x?.geoFence?.area?.coordinates) ?? [];
      const routePositions = this.tripsRecordPositions
          .reduce((acc, route) => [...acc, ...route], [])
          .map(position => entityDataManager.getGoogleGeoByPosition(position));
      const allPositions = [...geoFencePositions, ...routePositions].filter(x => x != null);

      if (!allPositions.length) {
        return;
      }

      const newBoundary = GoogleMapsManager.getMapBoundsByPositions(allPositions);
      this.$refs.googleMapRef?.map?.fitBounds(newBoundary);
    },
    async loadTripsForMapIfAllowed() {
      if (!this.taskFinished && !this.taskInProgress) {
        return;
      }

      await this.loadTripsForMap(true);
    },
    async loadTripsForMap(force = false) {
      if (!force && !this.taskFinished && !this.taskInProgress && !this.readOnly) {
        return;
      }
      if (this.readOnly || !this.vehicle?.deviceId || !this.startDate || !this.scheduleDate) {
        return;
      }

      const data = await WorkflowManager.getTripsMapDataWithoutStore(this.vehicle.deviceId, this.startDate, this.scheduleDate, false);
      this.initTripsForMap(data.tripsData, data.tripsRecordPositions)
    },
    initTripsForMap(tripsData, tripsRecordPositions) {
      this.tripsData = tripsData;
      this.tripsRecordPositions = tripsRecordPositions;
      this.selectedTrip = null;
      setTimeout(() => this.selectedPosition = this.sliderPositions[0]?.value ?? null);
    },
    async loadTripsForMapForce() {
      await this.loadTripsForMap(true);
      this.onMapReady();
    },
    onTripSelected(trip) {
      this.selectTrip(trip.index);
      this.selectedRouteMouseMove(trip);
    },
    selectTrip(selectedIndex) {
      if (this.selectedTrip?.index === selectedIndex) {
        return;
      }

      this.$refs.mapRoutes?.forEach(route => {
        if (route.index === selectedIndex) {
          this.selectedTrip = route;
          return;
        }

        route.deselectPosition();
      });
    },
    selectedRouteMouseMove(route) {
      this.selectedPosition = route.selectedPosition?.id ?? null;
    },
    onPositionSliderChange(value, sliderPosition) {
      if (!sliderPosition) {
        return;
      }

      const {info} = sliderPosition;
      this.selectTrip(info.tripIndex);
      if (!this.selectedTrip) {
        return;
      }

      this.selectedTrip.selectPositionById(value);
      this.selectedRouteMouseMove(this.selectedTrip);
      setTimeout(() => this.selectedTrip.initializeInfoWindow(), 10);
    },
    availableGeoFenceDataGridSelect(id, showInfoWindow = true) {
      (this.$refs.geoFenceAreasRef ?? []).forEach(x => x.id !== id && x.closeInfoWindow());
      this.focusGeoFenceById(id, showInfoWindow);
      this.selectedAvailableGeoFenceId = id;
      this.selectedUserGeoFenceId = null;
    },
    selectedGeoFenceDataGridSelect(id) {
      (this.$refs.geoFenceAreasRef ?? []).forEach(x => x.id !== id && x.closeInfoWindow());
      this.focusGeoFenceById(id);
      this.selectedUserGeoFenceId = id;
      this.selectedAvailableGeoFenceId = id;
    },
    async geoFenceDataAddOnList(id) {
      this.availableGeoFenceDataGridSelect(id, false);
      if (this.addedGeoFenceAreas.length > 0 && this.addedGeoFenceAreas[this.addedGeoFenceAreas.length - 1]?.geoFence?.id === id) {
        return;
      }

      const addedGeoFence = this.userGeoFences.find(x => x.id === id);

      const areaToAdd = {geoFence: addedGeoFence, visited: false, visitTimestamp: null};
      this.addedGeoFenceAreas.push(areaToAdd);
    },
    cancelGeoFenceCreate() {
      this.newGeoFenceData = null;
    },
    addGeoFenceAtMapCenter() {
      const mapCenter = this.$refs.googleMapRef?.map?.center;
      if (!mapCenter) {
        return;
      }

      this.newGeoFenceData = {
        type: GeoFenceType.Circle,
        id: 0,
        name: '',
        description: '',
        area: {
          radius: parseInt(process.env.VUE_APP_NEW_GEO_FENCE_CIRCLE_RADIUS, 10),
          coordinates: GoogleMapsManager.getCoordinatesFromGoogleData(mapCenter),
        },
        attributes: {
          color: GeoFenceColor.Green,
        },
      };
      setTimeout(() => {
        this.$refs.newGeofenceAreaRef?.showInfoWindow();
        this.$refs.newGeofenceAreaRef?.editGeoFence();
      });
    },
    onNewGeoFenceCreated() {
      this.newGeoFenceData = null;
    },
    async geoFenceDataRemoveFromList(item) {
      if (this.addedGeoFenceAreas.find(x => x.geoFence.id === item.id)) {
        let index = (item?.numbering ?? 0) -1; // convert from nonzero based to zero based index
        if (index !== -1) {
          this.addedGeoFenceAreas.splice(index, 1);

          // in case last element - finish execution
          if (index === this.addedGeoFenceAreas.length) {
            return;
          }

          // in case the next element is the same as the previous - it should be deleted also
          const previousGeoFence = this.addedGeoFenceAreas[index - 1]?.geoFence;
          const changedGeoFence = this.addedGeoFenceAreas[index].geoFence;
          if (previousGeoFence != null && changedGeoFence?.id === previousGeoFence?.id) {
            this.addedGeoFenceAreas.splice(index - 1, 1);
          }
        }
      }
    },
    async copyTask() {
      const task = entityDataManager.getById(this.tasks, this.copyId);
      if (new Date(task.startDate) <= new Date()) {
        task.startDate = moment().startOf('day').add(1, 'day').toDate();
        task.scheduleDate = moment().endOf('day').add(1, 'day').toDate();
      }
      this.loadTaskData(task);
      await this.loadGeoFences(task);
      await this.loadTripsForMapIfAllowed();

      this.onMapReady();
    },
    acceptTask() {
      this.$store.dispatch(actions.ACCEPT_TASK, {id: this.data.id, key: this.secretKey})
          .then(() => {
            this.$emit('task-accepted');
            notification.success(this.$t('generic.save.success'));
          })
          .catch(e => notification.error(this.$t(e.message)));
    },
    async onDragChange(changedData) {
      const draggedItem = this.addedGeoFenceAreas[changedData.oldIndex];
      const changedItem = this.addedGeoFenceAreas[changedData.newIndex];
      this.addedGeoFenceAreas[changedData.newIndex] = draggedItem;
      this.addedGeoFenceAreas[changedData.oldIndex] = changedItem;
    },
    onDriverUpdated() {
      this.driverPhoneNumber = this.driver?.phoneNumber ?? '';
    },
    onVehicleUpdated() {
      setTimeout(() => this.center = entityDataManager.getGoogleGeoByPosition(this.selectedVehicleCurrentPosition));
    },
    async saveTask() {
      if (!this.isCreateMode && !this.isEditMode) {
        return;
      }

      const taskData = {};
      if (this.isEditMode) {
        taskData.id = this.data?.id;
      }

      taskData.name = this.name;
      taskData.description = this.description;
      taskData.startDate =  dataManipulator.dateTimeToString(this.startDate);
      taskData.scheduleDate =  dataManipulator.dateTimeToString(this.scheduleDate);
      taskData.status = this.status;
      taskData.vehicleId = this.vehicle?.id;
      taskData.driverId = this.driver?.id;
      taskData.driverPhoneNumber = this.driverPhoneNumber ?? '';
      taskData.taskGeofences = this.addedGeoFenceAreasMappedToDto;

      const action = this.isCreateMode ? actions.CREATE_TASK : actions.UPDATE_TASK;

      await this.$store.dispatch(action, taskData)
          .then(async data => {
            this.$emit('task-saved', data);

            if (this.driver && !this.driver.phoneNumber) {
              await this.$store.dispatch(actions.GET_DRIVERS);
            }

            return Promise.resolve(data);
          })
          .then(async data => {
            if (this.isCreateMode) {
              await this.$store.dispatch(actions.GET_TASKS);
            }

            return Promise.resolve(data);
          })
          .then(data => {
            notification.success(this.$t('generic.save.success'));

            if (this.isCreateMode) {
              this.$router.push({path: '/task-planner/all-tasks', query: {id: data.id}});
            }
          })
          .catch(e => notification.error(this.$t(e.message)));
    },
    cancelButtonClick() {
      popups.yesNoConfirmation(this.$swal, this.$t('generic.edit.confirm'))
          .then(async result => {
            if (!result.isConfirmed) {
              return;
            }

            this.loadTaskData(this.data);
            await this.loadGeoFences(this.data);
          });
    },
    openGeoFence(id) {
      const geoFence = this.getAreaById(id);
      this.geoFenceAreaClick(geoFence);
    },
    geoFenceAreaClick(area) {
      (this.$refs.geoFenceAreasRef ?? []).forEach(x => x.id !== area.id && x.closeInfoWindow());
      area?.showInfoWindow();
      this.selectedAvailableGeoFenceId = area.id;
      this.selectedUserGeoFenceId = null;
      this.scrollAvailableGeoFencesDataGrid();
    },
    loadTaskData(task) {
      this.name = task.name;
      this.description = task.description;
      this.status = task.status;
      this.startDate = new Date(task.startDate);
      this.scheduleDate = new Date(task.scheduleDate);
      this.vehicle = entityDataManager.getById(this.vehicles, task.vehicleId);
      this.driver = entityDataManager.getById(this.drivers, task.driverId);
      this.driverPhoneNumber = task.driverPhoneNumber;
    },
    async loadGeoFences(task) {
      this.addedGeoFenceAreas = [];
      for (let i = 0; i < task.taskGeofences.length; i++) {
        let currentGeoFence = entityDataManager.getById(this.userGeoFences, task.taskGeofences[i].geofenceId);
        if (!currentGeoFence) {
          currentGeoFence = { id: task.taskGeofences[i].geofenceId };
        }
        const areaToAdd = {geoFence: currentGeoFence, visited: task.taskGeofences[i].visited, visitTimestamp: task.taskGeofences[i].visitTimestamp};

        this.addedGeoFenceAreas.push(areaToAdd);
      }
    },
    onGeoFenceSaved(geoFence) {
      this.syncAddedGeoFenceAreas();
      this.focusGeoFenceById(geoFence.id, false);
    },
    syncAddedGeoFenceAreas() {
      this.addedGeoFenceAreas = this.addedGeoFenceAreas.map(area => {
        const geoFence = entityDataManager.getById(this.userGeoFences, area.geoFence?.id);
        return {
          ...area,
          geoFence: geoFence ?? area.geoFence
        };
      });
    },
    focusGeoFenceById(id, showInfoWindow = true) {
      const area = this.getAreaById(id);
      if (showInfoWindow) {
        area?.showInfoWindow();
      }
      this.centerAreaOnMap(area);
    },
    centerAreaOnMap(area) {
      if (area) {
        this.center = area.getCenter();
        this.$refs.googleMapRef?.map?.setZoom(18);
      }
    },
    scrollAvailableGeoFencesDataGrid() {
      if (this.$refs.availableGeoFencesList) {
        setTimeout(() => this.$refs.availableGeoFencesList.scrollToSelectedRow());
      }
    },
    getAreaById(id) {
      return (this.$refs.geoFenceAreasRef ?? []).find(area => area.id === id)
    }
  }
}

</script>

<style lang="scss">

.drivers {
  width: auto !important;
}

.vehicles {
  width: auto !important;
}

.geo-fence-list {
  max-height: calc(100vh - 500px) !important;
  height: calc(100vh - 500px) !important;

  &.available-geofences {
    table {
      tr {
        th, td {
          &:last-of-type {
            width: 23% !important;
          }
        }
      }
    }
  }

  &.selected-geofences {
    table {
      tr {
        th, td {
          &:last-of-type {
            width: 23% !important;
          }
          &:first-of-type {
            width: 20% !important;
            padding-left: 10px !important;
            padding-right: 0 !important;
          }
        }

        &.visited {
          background-color: #90ee907d;
        }

        &.not-visited {
          background-color: #ff00004d;
        }
      }
    }
  }
}

.button-save {
  float: right;
  margin-right: 20px;
}

.map-container {
  margin-top: 0;
  margin-right: 20px;
  width: 100%;
  container-type: inline-size;

  .task-map-control {
    margin: 10px 0 0 10px;
  }

  .task-slider-control {
    margin: 10px 0 0 10px;
    width: calc(100% - 443px);
  }

  .task-positions-slider {
    width: 100%;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.96);
    box-shadow: 0 2px 10px rgba(32, 33, 36, 0.2);
    padding: 15px 12px 10px !important;
  }

  .map {
    width: 100%;
    height: 100vh;
    border-radius: 15px;
  }

  .geofence-label {
    top: 30px;
  }
}
</style>
